import React, { useEffect, useState } from 'react';
import {
    IonPage,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonRadioGroup,
    IonItem,
    IonRadio,
    IonLabel,
} from '@ionic/react';

const logicTypes = ['direction', 'object-rename', 'reverse-code', 'object-image-rename'];

const directions = ['East', 'West', 'North', 'South'];
const objects = ['Cup', 'Spoon', 'Knife', 'Plate', 'Pencil', 'Eraser', 'Sharpener', 'Mug'];
const wordList = ['MONKEY', 'BUTTERFLY', 'ELEPHANT', 'CROCODILE', 'ZEBRA', 'GIRAFFE'];

const objectImageMap: { [key: string]: string } = {
    Cup: 'cup.png',
    Spoon: 'spoon.png',
    Knife: 'knife.png',
    Plate: 'plate.png',
    Pencil: 'pencil.png',
    Eraser: 'eraser.png',
    Sharpener: 'sharpener.png',
    Mug: 'mug.png',
};

function getImageSrc(imageName: string) {
    return `/assets/images/objects/${imageName}`; // adjust path based on your assets structure
}

function shuffleArray<T>(array: T[]): T[] {
    return [...array]
        .map(a => [Math.random(), a] as [number, T])
        .sort((a, b) => a[0] - b[0])
        .map(a => a[1]);
}

function generateQuestion() {
    const type = logicTypes[Math.floor(Math.random() * logicTypes.length)];

    if (type === 'direction') {
        const shuffled = shuffleArray(directions);
        const map = {
            East: shuffled[0],
            South: shuffled[1],
            North: shuffled[2],
        };
        const question = `If East is called ${map.East}, South is called ${map.South}, and North is called ${map.North}, then where does the sun rise?`;
        const actualAnswer = map.East;
        const combinedOptions = Array.from(new Set([...Object.values(map), ...directions]));
        while (combinedOptions.length < 4) {
            const filler = directions.find(dir => !combinedOptions.includes(dir));
            if (filler) combinedOptions.push(filler);
        }
        const options = shuffleArray(combinedOptions).slice(0, 4);
        return { type, question, options, correct: actualAnswer };
    }

    if (type === 'object-rename') {
        const shuffled = shuffleArray(objects);
        const A = shuffled[0];
        const B = shuffled[1];
        const C = shuffled[2];
        const D = shuffled[3];
        const mapping = {
            [A]: B,
            [B]: C,
            [C]: D,
        };
        const question = `If ${A} is called ${B}, ${B} is called ${C}, and ${C} is called ${D}, then what do we use to drink tea or coffee?`;
        const actualAnswer = mapping[mapping[mapping[A]]] || A;
        const options = shuffleArray([A, B, C, D]);
        return { type, question, options, correct: actualAnswer };
    }

    if (type === 'reverse-code') {
        const word = wordList[Math.floor(Math.random() * wordList.length)];
        const coded = word.split('').reverse().join('');
        const target = wordList[0];
        const correct = target.split('').reverse().join('');
        const distractors = wordList
            .filter(w => w !== target)
            .map(w => w.split('').reverse().join(''))
            .slice(0, 3);
        const options = shuffleArray([correct, ...distractors]);
        const question = `If the code for "${word}" is "${coded}", then what is the code for "${target}"?`;
        return { type, question, options, correct };
    }

    if (type === 'object-image-rename') {
        const shuffled = shuffleArray(objects);
        const A = shuffled[0];
        const B = shuffled[1];
        const C = shuffled[2];
        const D = shuffled[3];
        const mapping = {
            [A]: B,
            [B]: C,
            [C]: D,
        };
        const question = `If ${A} is called ${B}, ${B} is called ${C}, and ${C} is called ${D}, then what do we use to drink tea or coffee?`;
        const actualAnswer = mapping[mapping[mapping[A]]] || A;
        const imageOptions = shuffleArray([A, B, C, D]).map(item => objectImageMap[item]);
        const correctImage = objectImageMap[actualAnswer];
        return { type, question, options: imageOptions, correct: correctImage };
    }

    return { type: '', question: '', options: [], correct: '' };
}

const CodingDecodingComponent: React.FC = () => {
    const [questionData, setQuestionData] = useState<{
        type: string;
        question: string;
        options: string[];
        correct: string;
    }>({ type: '', question: '', options: [], correct: '' });

    const [selected, setSelected] = useState<string | null>(null);
    const [showImage, setShowImage] = useState(false);

    const generateNew = () => {
        const newQuestion = generateQuestion();
        setQuestionData(newQuestion);
        setSelected(null);
    };

    const handleSelect = (value: string) => {
        if (!questionData) return;
        setSelected(value);
        if (value === questionData.correct) {
            setShowImage(true);
            setTimeout(() => {
                setShowImage(false);
                generateNew();
            }, 2500);
        }
    };

    useEffect(() => {
        generateNew();
    }, []);

    return (
        <div className="contentPart fillPreposition additionWord">
            {!showImage ? (
                <>
                    <h3>{questionData.question}</h3>
                    <IonRadioGroup value={selected} onIonChange={(e) => handleSelect(e.detail.value)}>
                        {questionData.options.map((opt, idx) => (
                            <IonItem
                                key={idx}
                                className={`mygrid grid-item ${
                                    selected === opt
                                        ? opt === questionData.correct
                                            ? 'correct'
                                            : 'errorOptions'
                                        : ''
                                }`}
                            >
                                {questionData.type === 'object-image-rename' ? (
                                    <>
                                        <img
                                            src={getImageSrc(opt)}
                                            alt={`Option ${idx + 1}`}
                                            style={{ height: 60, marginRight: 10 }}
                                        />
                                        <IonRadio slot="start" value={opt} />
                                    </>
                                ) : (
                                    <>
                                        <IonLabel>
                                            {String.fromCharCode(65 + idx)}) {opt}
                                        </IonLabel>
                                        <IonRadio slot="start" value={opt} />
                                    </>
                                )}
                            </IonItem>
                        ))}
                    </IonRadioGroup>
                </>
            ) : (
                <div className="showMiddle">
                    <img
                        src={process.env.REACT_APP_WELDONE_LINK ?? ''}
                        alt="Well Done"
                        style={{ height: 150 }}
                    />
                </div>
            )}
        </div>
    );
};

export default CodingDecodingComponent;
